/**
 * 「食べに行く」ごほうびモーダル。
 *
 * 進化一覧のタイルをクリックすると開き、
 * その料理の紹介 + 地図/検索リンクで外部遷移する。
 */

import type { GameContext } from '../game/GameContext';
import { getRewardStatus } from '../game/config/rewardStatus';
import type { StageItemDefinition } from '../game/config/types';

export class RewardModal {
  private readonly context: GameContext;
  private readonly root: HTMLElement | null;
  private readonly iconEl: HTMLImageElement | null;
  private readonly titleEl: HTMLElement | null;
  private readonly subtitleEl: HTMLElement | null;
  private readonly headlineEl: HTMLElement | null;
  private readonly tipEl: HTMLElement | null;
  private readonly areaEl: HTMLElement | null;
  private readonly linksEl: HTMLElement | null;
  private readonly closeBtn: HTMLButtonElement | null;
  private currentItem: StageItemDefinition | null = null;

  constructor(context: GameContext, root: ParentNode = document) {
    this.context = context;
    this.root = root.querySelector<HTMLElement>('[data-ui="reward-modal"]');
    this.iconEl = root.querySelector<HTMLImageElement>('[data-ui="reward-icon"]');
    this.titleEl = root.querySelector<HTMLElement>('[data-ui="reward-title"]');
    this.subtitleEl = root.querySelector<HTMLElement>('[data-ui="reward-subtitle"]');
    this.headlineEl = root.querySelector<HTMLElement>('[data-ui="reward-headline"]');
    this.tipEl = root.querySelector<HTMLElement>('[data-ui="reward-tip"]');
    this.areaEl = root.querySelector<HTMLElement>('[data-ui="reward-area"]');
    this.linksEl = root.querySelector<HTMLElement>('[data-ui="reward-links"]');
    this.closeBtn = root.querySelector<HTMLButtonElement>('[data-ui="reward-close"]');

    this.closeBtn?.addEventListener('click', () => {
      this.hide();
      this.onClose?.();
    });
    this.root?.addEventListener('click', (event) => {
      if (event.target !== this.root) return;
      this.hide();
      this.onClose?.();
    });
  }

  /** モーダルが閉じられたときに呼ばれるコールバック。 */
  onClose?: () => void;

  open(item: StageItemDefinition): void {
    if (!this.root) {
      console.warn('[RewardModal] root 要素が見つかりません');
      return;
    }

    const status = getRewardStatus(
      item,
      this.context.storage.seenItemIds,
      this.context.storage.totalScore,
    );
    if (!status.available) {
      // 開けない状態でのクリックは無視（UI的には発生しにくいが念のため）
      return;
    }

    this.currentItem = item;

    if (this.iconEl) {
      this.iconEl.src = this.context.icons.iconUrl(item, 160);
      this.iconEl.alt = item.name;
    }
    if (this.titleEl) this.titleEl.textContent = item.name;
    if (this.subtitleEl) {
      this.subtitleEl.textContent = item.reward
        ? `${item.reward.area}のおすすめ`
        : this.context.stage.regionName;
    }
    if (this.headlineEl) {
      this.headlineEl.textContent = item.reward?.headline ?? item.description ?? '';
    }
    if (this.tipEl) {
      this.tipEl.textContent = item.reward?.tip ?? '';
      this.tipEl.style.display = item.reward?.tip ? '' : 'none';
    }
    if (this.areaEl) {
      this.areaEl.textContent = item.reward?.area ?? '';
    }

    this.renderLinks(item);

    this.root.dataset.visible = 'true';
    this.closeBtn?.focus();
  }

  private renderLinks(item: StageItemDefinition): void {
    if (!this.linksEl) return;
    this.linksEl.replaceChildren();

    const official = (item.reward?.official ?? []).map((link) => ({ ...link, official: true }));
    const links = [...official, ...(item.reward?.links ?? []).map((link) => ({ ...link, official: false }))];
    if (links.length === 0) {
      this.linksEl.textContent = 'リンクが設定されていません';
      return;
    }

    const list = document.createElement('ul');
    list.className = 'reward-links';
    links.forEach((link) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = link.url;
      a.textContent = link.official ? `公式｜${link.label}` : link.label;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = link.official ? 'btn btn--primary reward-links__official' : 'btn btn--primary';
      a.setAttribute('aria-label', `${link.label}（新しいタブで開く）`);
      li.append(a);
      list.append(li);
    });
    this.linksEl.append(list);
  }

  hide(): void {
    if (!this.root) return;
    this.root.dataset.visible = 'false';
    this.currentItem = null;
  }

  isOpen(): boolean {
    return this.root?.dataset.visible === 'true';
  }

  get current(): StageItemDefinition | null {
    return this.currentItem;
  }
}
